import React, { PureComponent } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';
import { useRouterActions } from 'react-resource-router';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import Button from '@atlaskit/button/standard-button';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { BuildSummary, BuildsSummaryProps } from '@atlassian/bitkit-builds';

import GenericMessage from 'src/components/generic-message';
import { ConnectedExpander } from 'src/sections/repository/sections/pull-request/components/connected-expander';

import messages from './build-status-card.i18n';
import BuildStatusPipelinesPromotion from './build-status-pipelines-promotion';
import {
  withBuildStatus,
  BuildStatusResultProp,
} from './services/build-status-service/build-status-service';
import { getBuildStatuses } from './utils';

type BuildStatusProps = {
  intl: InjectedIntl;
  isCollapsed?: boolean;
} & BuildStatusResultProp;
const BUILD_CARD_COLLAPSED_LOCALSTORAGE_KEY = 'build.card.collapsed';

const BuildSummaryWithRouter = (props: BuildsSummaryProps) => {
  const { push } = useRouterActions();
  const enableClientSideLinking = FeatureGates.checkGate(
    StatsigFeatureKeys.enableClientSideLinking
  );

  return (
    <BuildSummary
      {...props}
      routerPush={enableClientSideLinking ? push : undefined}
    />
  );
};

export class BaseBuildStatusCard extends PureComponent<BuildStatusProps> {
  static defaultProps = {
    isCollapsed: false,
  };

  renderLabels = () => {
    const {
      buildStatusResult: { builds },
      intl,
    } = this.props;
    const { FAILED, SUCCESSFUL } = getBuildStatuses(builds);

    if (builds.length === 0) {
      const countNoBuilds = 0;

      return {
        rich: (
          <FormattedMessage
            {...messages.zeroBuilds}
            values={{
              formattedCount: <strong>{countNoBuilds}</strong>,
            }}
          />
        ),
        plain: intl.formatMessage(messages.zeroBuilds, {
          formattedCount: countNoBuilds,
        }),
      };
    }

    if (FAILED) {
      const countFailed = `${FAILED} of ${builds.length}`;

      return {
        rich: (
          <FormattedMessage
            {...messages.failedStatus}
            values={{
              total: builds.length,
              formattedCount: <strong>{countFailed}</strong>,
            }}
          />
        ),
        plain: intl.formatMessage(messages.failedStatus, {
          total: builds.length,
          formattedCount: countFailed,
        }),
      };
    }

    const countSuccess = `${SUCCESSFUL} of ${builds.length}`;

    return {
      rich: (
        <FormattedMessage
          {...messages.passedStatus}
          values={{
            total: builds.length,
            formattedCount: <strong>{countSuccess}</strong>,
          }}
        />
      ),
      plain: intl.formatMessage(messages.passedStatus, {
        total: builds.length,
        formattedCount: countSuccess,
      }),
    };
  };

  renderExpanderContent = () => {
    const { builds, hasError, refetch, repositoryFullSlug, hasStatuses } =
      this.props.buildStatusResult;

    if (hasError) {
      return (
        <GenericMessage
          iconType="warning"
          title={<FormattedMessage {...messages.errorHeading} />}
        >
          <CustomThemeButton appearance="link" onClick={refetch}>
            <FormattedMessage {...messages.errorAction} />
          </CustomThemeButton>
        </GenericMessage>
      );
    }

    if (builds.length) {
      return <BuildSummaryWithRouter builds={builds} />;
    }

    if (!!repositoryFullSlug && !hasStatuses) {
      return (
        <BuildStatusPipelinesPromotion
          repositoryFullSlug={repositoryFullSlug}
        />
      );
    }

    return null;
  };

  render() {
    const {
      isCollapsed,
      intl,
      buildStatusResult: { isLoading },
    } = this.props;
    const { plain: plainLabel, rich: richLabel } = this.renderLabels();
    const icon = <RefreshIcon label="" />;

    if (isCollapsed) {
      return (
        <Tooltip position="left" content={plainLabel}>
          <Button appearance="subtle" iconBefore={icon} />
        </Tooltip>
      );
    }

    return (
      <ConnectedExpander
        localStorageKey={BUILD_CARD_COLLAPSED_LOCALSTORAGE_KEY}
        icon={icon}
        dataTestId="build-status-card"
        isLoading={isLoading}
        label={richLabel}
        sectionAriaLabel={intl.formatMessage(messages.sectionAriaLabel)}
        headingAriaLabel={plainLabel}
      >
        {this.renderExpanderContent()}
      </ConnectedExpander>
    );
  }
}

export const BuildStatusCard = injectIntl(withBuildStatus(BaseBuildStatusCard));
